import { z } from "zod";

import { webEnvironment } from "./env";

const errorSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.unknown()),
  }),
});

const questionSchema = z.object({
  key: z.string(),
  position: z.number().int().positive(),
  supports_defer: z.boolean(),
});

const stepSchema = z.object({
  key: z.string(),
  kind: z.enum(["NARRATIVE", "PERSPECTIVE", "COMPLETION"]),
  position: z.number().int().positive(),
  shows_progress: z.boolean(),
  assistant_available: z.boolean(),
  questions: z.array(questionSchema),
});

const compensationSchema = z.object({
  version: z.string(),
  currency: z.string().length(3),
  contractual: z.literal(false),
  illustration_annual_revenue_minor: z.number().int().nonnegative(),
  illustration_total_minor: z.number().int().nonnegative(),
  direct_commission: z.array(
    z.object({
      subscription_year: z.number().int().positive(),
      rate_basis_points: z.number().int().nonnegative(),
      illustration_minor: z.number().int().nonnegative(),
    }),
  ),
  network_overrides: z.array(
    z.object({
      role: z.string(),
      rate_basis_points: z.number().int().nonnegative(),
    }),
  ),
  component_keys: z.array(z.string()),
});

const programSchema = z.object({
  key: z.string(),
  version: z.string(),
  founding: z.boolean(),
  final_position: z.number().int().positive(),
  steps: z.array(stepSchema),
  compensation: compensationSchema,
  ambassador_types: z.array(z.string()),
  ambassador_grades: z.array(z.string()),
  network_roles: z.array(z.string()),
});

export const journeySchema = z.object({
  status: z.enum([
    "ISSUED",
    "OPENED",
    "IN_PROGRESS",
    "COMPLETED",
    "EXPIRED",
    "REVOKED",
  ]),
  candidate_given_name: z.string(),
  network_role: z.string(),
  primary_type: z.string().nullable(),
  territory_code: z.string(),
  segment_key: z.string().nullable(),
  founding: z.boolean(),
  resume_position: z.number().int().positive(),
  highest_completed_position: z.number().int().nonnegative(),
  assistant_enabled: z.boolean(),
  program: programSchema,
  responses: z.array(
    z.object({
      question_key: z.string(),
      disposition: z.enum(["ANSWERED", "DEFERRED_TO_INTERVIEW", "SKIPPED"]),
      answer_text: z.string().nullable(),
      updated_at: z.string(),
    }),
  ),
  meeting: z
    .object({ scheduled_at: z.string(), location: z.string().nullable() })
    .nullable(),
});

const assistantSchema = z.object({
  outcome: z.enum(["ANSWERED", "NO_APPROVED_ANSWER", "UNAVAILABLE"]),
  answer: z.string().nullable(),
});

export type Journey = z.infer<typeof journeySchema>;
export type JourneyStep = z.infer<typeof stepSchema>;
export type JourneyProgram = z.infer<typeof programSchema>;
export type Compensation = z.infer<typeof compensationSchema>;
export type AssistantReply = z.infer<typeof assistantSchema>;
export type Disposition = "ANSWERED" | "DEFERRED_TO_INTERVIEW" | "SKIPPED";

export class JourneyApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly invitationStatus: string | null;

  constructor(
    status: number,
    code: string,
    message: string,
    invitationStatus: string | null = null,
  ) {
    super(message);
    this.name = "JourneyApiError";
    this.status = status;
    this.code = code;
    this.invitationStatus = invitationStatus;
  }
}

const base = `${webEnvironment.VITE_ZHATURA_API_ORIGIN}/api/v1/ambassador`;

async function send(
  path: string,
  method: "POST" | "PUT",
  body: Record<string, unknown>,
): Promise<unknown> {
  let response: Response;
  try {
    response = await fetch(`${base}${path}`, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "omit",
      referrerPolicy: "no-referrer",
      cache: "no-store",
    });
  } catch {
    throw new JourneyApiError(0, "NETWORK_UNAVAILABLE", "network-unavailable");
  }
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const parsed = errorSchema.safeParse(payload);
    const detail = parsed.success
      ? parsed.data.error.details.status
      : undefined;
    throw new JourneyApiError(
      response.status,
      parsed.success ? parsed.data.error.code : "UNKNOWN",
      parsed.success ? parsed.data.error.message : "request-failed",
      typeof detail === "string" ? detail : null,
    );
  }
  return payload;
}

export async function openJourney(credential: string): Promise<Journey> {
  return journeySchema.parse(await send("/journey", "POST", { credential }));
}

export async function startJourney(credential: string): Promise<Journey> {
  return journeySchema.parse(
    await send("/journey/start", "POST", { credential }),
  );
}

export async function viewStep(
  credential: string,
  stepKey: string,
): Promise<Journey> {
  return journeySchema.parse(
    await send(`/journey/steps/${encodeURIComponent(stepKey)}/view`, "POST", {
      credential,
    }),
  );
}

export async function completeStep(
  credential: string,
  stepKey: string,
): Promise<Journey> {
  return journeySchema.parse(
    await send(
      `/journey/steps/${encodeURIComponent(stepKey)}/complete`,
      "POST",
      { credential },
    ),
  );
}

export async function saveResponse(
  credential: string,
  questionKey: string,
  disposition: Disposition,
  answerText: string | null,
): Promise<Journey> {
  return journeySchema.parse(
    await send(`/journey/responses/${encodeURIComponent(questionKey)}`, "PUT", {
      credential,
      disposition,
      answer_text: disposition === "ANSWERED" ? answerText : null,
    }),
  );
}

export async function askZhatura(
  credential: string,
  question: string,
  stepKey: string | null,
): Promise<AssistantReply> {
  return assistantSchema.parse(
    await send("/journey/assistant", "POST", {
      credential,
      question,
      step_key: stepKey,
    }),
  );
}
